import { createContext, useRef, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
// firestore
import { doc, query, setDoc, orderBy, deleteDoc, collection, onSnapshot, Timestamp } from "firebase/firestore";
import { db } from "../services/firebase";
//
import axios from "../utils/axios";
// actions
import actionTypes from "../common/actionTypes";
// context
import useAuthContext from "../hooks/useAuthContext";
import useActivityLogsContext from "../hooks/useActivityLogsContext";
// reducers
import { sellingPlanReducer } from "../reducers";
// hooks
import useCustomReducer from "../hooks/useCustomReducer";
// constants
import { APP_ROUTES, DATABASE_STATUTES } from "../common/constants";
import { LOG_TYPES } from "./ActivityLogsContext";

const SHOPS = "Shops";
const SELLING_PLAN_GROUPS = "SellingPlanGroups";
const SHOPIFY_SELLING_PLANS = "shopify/sellingPlans";

const initialState = {
  data: [],
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const SellingPlansContext = createContext({
  ...initialState,
  createPlan: () => Promise.resolve(),
  deletePlan: () => Promise.resolve(),
  deletePlans: () => Promise.resolve(),
  editPlan: () => Promise.resolve(),
  copyPlan: () => Promise.resolve(),
  getSellingPlanById: () => Promise.resolve(),
});

const SellingPlansProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(sellingPlanReducer, initialState);

  const sellingPlansCollectionRef = useRef(null);

  const { user } = useAuthContext();
  const { writeLog } = useActivityLogsContext();

  const { pathname } = useLocation();
  const fetchingIsActive =
    pathname.startsWith(APP_ROUTES.PRODUCTS.ROOT) ||
    pathname.startsWith(APP_ROUTES.SETTINGS.SELLING_PLANS) ||
    pathname.startsWith(APP_ROUTES.AUTOMATIONS.TAGS);

  useEffect(() => {
    if (user?.domain && fetchingIsActive) {
      try {
        dispatch({
          type: actionTypes.FETCH_SELLING_PLANS,
        });

        sellingPlansCollectionRef.current = collection(db, SHOPS, user.domain, SELLING_PLAN_GROUPS);

        return onSnapshot(query(sellingPlansCollectionRef.current, orderBy("createdAt", "asc")), (snapshot) => {
          dispatch({
            type: actionTypes.FETCH_SELLING_PLANS_SUCCESS,
            payload: snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            })),
          });
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_SELLING_PLANS_FAILURE,
          payload: error,
        });
      }
    }
  }, [user?.domain, fetchingIsActive]);

  const createPlan = async ({ name, description, sellingPlanName, sellingPlanOptions, settings }) => {
    const sellingPlan = {
      name,
      description,
      sellingPlanName,
      sellingPlanOptions,
      createdAt: Timestamp.now(),
      ...settings,
    };

    try {
      dispatch({
        type: actionTypes.CREATE_PLAN,
      });

      const response = await axios.post(SHOPIFY_SELLING_PLANS, { sellingPlan });
      console.log("createPlan response", response);

      const id = response.data.sellingPlanGroupCreate.sellingPlanGroup.id.split("/").pop();
      const sellingPlanId = response.data.sellingPlanGroupCreate.sellingPlanGroup.sellingPlans.edges[0].node.id;
      sellingPlan.sellingPlanId = sellingPlanId;

      await setDoc(doc(sellingPlansCollectionRef.current, id), sellingPlan);

      dispatch({
        type: actionTypes.CREATE_PLAN_SUCCESS,
      });

      await writeLog(LOG_TYPES.ADD_SELLING_PLAN, name);

      return Promise.resolve({ sellingPlanGroupId: id, sellingPlanId: sellingPlanId.split("/").pop() });
    } catch (error) {
      console.log("%c [Error]", "color: red; font-weight: bold;", error);
      dispatch({
        type: actionTypes.CREATE_PLAN_FAILURE,
      });

      return Promise.reject();
    }
  };

  const deletePlan = useCallback(
    async ({ id }) => {
      try {
        await axios.delete(`${SHOPIFY_SELLING_PLANS}/${id}`);

        await deleteDoc(doc(sellingPlansCollectionRef.current, id));

        const sellingPlan = state.data.find((sellingPlan) => sellingPlan.id === id);
        await writeLog(LOG_TYPES.DELETE_SELLING_PLAN, sellingPlan.name);

        return Promise.resolve();
      } catch (error) {
        console.log("%c [Error]", "color: red; font-weight: bold;", error);
        return Promise.reject();
      }
    },
    [state.data, writeLog]
  );

  const editPlan = async ({ id, data }) => {
    try {
      await axios.post(`${SHOPIFY_SELLING_PLANS}/${id}`, { sellingPlan: data });

      await setDoc(doc(sellingPlansCollectionRef.current, id), data, {
        merge: true,
      });

      await writeLog(LOG_TYPES.UPDATE_SELLING_PLAN, data.name);

      return Promise.resolve();
    } catch (error) {
      console.log("%c [Error]", "color: red; font-weight: bold;", error);
      return Promise.reject();
    }
  };

  const copyPlan = async (targetPlanId) => {
    const targetPlan = state.data.find((elem) => elem.id === targetPlanId);

    const { sellingPlanGroupId } = await createPlan({
      name: `${targetPlan.name} copy`,
      description: targetPlan.description,
      sellingPlanName: targetPlan.sellingPlanName,
      sellingPlanOptions: targetPlan.sellingPlanOptions,
      settings: {
        billingPolicy: targetPlan.billingPolicy,
        shippingDate: targetPlan.shippingDate,
        discount: targetPlan.discount,
        discountType: targetPlan.discountType,
      },
    });

    return sellingPlanGroupId;
  };

  const getSellingPlanById = useCallback(
    (id) => {
      return state.data.find((elem) => elem.id === id);
    },
    [state.data]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      createPlan,
      deletePlan,
      editPlan,
      copyPlan,
      getSellingPlanById,
    }),
    [
      state,
      deletePlan,
      getSellingPlanById,
      // createPlan,
      // editPlan,
      // copyPlan,
    ]
  );

  return <SellingPlansContext.Provider value={memoizedState}>{children}</SellingPlansContext.Provider>;
};

export { SellingPlansContext, SellingPlansProvider };
